import { CLOUD_LEVELS, FIND_IDS, type CloudLevelId, type FindId } from '@sd/content';
import { hexKey, hexNeighbors, type Hex } from './hex';
import type { Rng } from './rng';
import type { DustCloud, StarSystem } from './system';
import { isPassable, type FleetState } from './fleet';

/**
 * Dust cloud exploration (GDD §7): finds left in every cloud, the shuffled
 * per-level bags, and the asteroid sites finds have revealed. Pure helpers;
 * the match drives them from `step`.
 */
export interface AsteroidSite {
  readonly id: string;
  readonly hex: Hex;
  /** The cloud whose scan revealed it. */
  readonly cloudId: string;
}

export interface ExplorationState {
  /** Finds still hidden in each cloud, by cloud id. */
  readonly findsLeft: Readonly<Record<string, number>>;
  /** Draw order of each level's bag; refilled and reshuffled when empty. */
  readonly bags: Readonly<Record<CloudLevelId, readonly FindId[]>>;
  readonly asteroids: readonly AsteroidSite[];
}

export interface ScanState {
  readonly cloudId: string;
  /** Ticks spent on the current find. */
  readonly progress: number;
}

export function createExploration(system: StarSystem): ExplorationState {
  return {
    findsLeft: Object.fromEntries(system.clouds.map((c) => [c.id, CLOUD_LEVELS[c.level].finds])),
    bags: { 1: [], 2: [], 3: [] },
    asteroids: [],
  };
}

export function cloudAt(system: StarSystem, h: Hex): DustCloud | undefined {
  let byHex = cloudIndex.get(system);
  if (!byHex) {
    byHex = new Map();
    for (const c of system.clouds)
      for (const x of c.hexes) if (!byHex.has(cellOf(x))) byHex.set(cellOf(x), c);
    cloudIndex.set(system, byHex);
  }
  return byHex.get(cellOf(h));
}

/**
 * The dust cloud on a hex that still holds finds (#103, the author's request
 * of 21.10.2026): a cloud scanned to the end disperses — it hides no fleets
 * and is gone from the map.
 */
export function liveCloudAt(
  system: StarSystem,
  findsLeft: Readonly<Record<string, number>> | undefined,
  h: Hex,
): DustCloud | undefined {
  const cloud = cloudAt(system, h);
  return cloud && (!findsLeft || (findsLeft[cloud.id] ?? 0) > 0) ? cloud : undefined;
}

/** A hex as one number, for lookups on the hot path (#50). */
const cellOf = (h: Hex) => (h.q + 1024) * 2048 + (h.r + 1024);
/** Clouds by hex, per map: the map never changes during a match. */
const cloudIndex = new WeakMap<StarSystem, Map<number, DustCloud>>();

/** A fresh, shuffled copy of a level's bag. */
export function fillBag(level: CloudLevelId, rng: Rng): FindId[] {
  const bag: FindId[] = [];
  for (const id of FIND_IDS) {
    for (let i = 0; i < CLOUD_LEVELS[level].bag[id]; i++) bag.push(id);
  }
  return rng.shuffle(bag);
}

/** Draws the next find of `level`, refilling the bag when it runs out. */
export function drawFind(
  exploration: ExplorationState,
  level: CloudLevelId,
  rng: Rng,
): { find: FindId; exploration: ExplorationState } {
  const bag = exploration.bags[level].length ? exploration.bags[level] : fillBag(level, rng);
  const [find, ...rest] = bag;
  if (!find) throw new Error(`empty find bag for level ${level}`);
  return { find, exploration: { ...exploration, bags: { ...exploration.bags, [level]: rest } } };
}

/**
 * Where a new asteroid appears: the first free hex of the cloud, else of its
 * rim. Free = passable, no planet, no other asteroid. Null if none.
 */
export function asteroidHex(
  system: StarSystem,
  exploration: ExplorationState,
  cloud: DustCloud,
): Hex | null {
  const taken = new Set([
    ...system.planets.map((p) => hexKey(p.hex)),
    ...exploration.asteroids.map((a) => hexKey(a.hex)),
  ]);
  const candidates = [...cloud.hexes, ...cloud.hexes.flatMap((h) => hexNeighbors(h))];
  const seen = new Set<string>();
  for (const h of candidates) {
    const k = hexKey(h);
    if (seen.has(k)) continue;
    seen.add(k);
    if (!taken.has(k) && isPassable(system, h)) return h;
  }
  return null;
}

/**
 * Fleets a player can see (GDD §7): everything, except other players'
 * fleets inside a dust cloud — unless the viewer has a fleet in that cloud
 * too. The full fog of war arrives with the client (#33).
 */
export function visibleFleets(
  system: StarSystem,
  fleets: readonly FleetState[],
  viewer: string,
): FleetState[] {
  const own = new Set(
    fleets
      .filter((f) => f.owner === viewer)
      .map((f) => cloudAt(system, f.hex)?.id)
      .filter((id): id is string => id !== undefined),
  );
  return fleets.filter((f) => {
    if (f.owner === viewer) return true;
    const cloud = cloudAt(system, f.hex);
    return !cloud || own.has(cloud.id);
  });
}
